import { clsx } from 'clsx'

import styles from 'components/chat-components/AgentMessage.module.css'
import { getContentSignature } from 'components/chat-components/AgentMessage.utils'
import { ContentRenderer } from 'components/chat-components/ContentRenderer'
import { AgentMessage as AgentMessageType } from 'modules/coaching/types'

import type { UploadedVideoSource } from 'components/chat-components/AgentMessage.types'
import type { ReactElement, RefObject } from 'react'
import type { MuxPlaybackEmbed } from 'utils/videoUrlResolver'
export interface AgentMessageProperties {
  message: AgentMessageType
  onSeekToTime?: (startTime: string) => void
  onPlaySegment?: (startTime: string) => void
  videoReference?: RefObject<HTMLVideoElement | null>
  videoUrl?: string
  mux?: MuxPlaybackEmbed
  videoDuration?: number
  videoSources?: UploadedVideoSource[]
  isShowFunctionCalls?: boolean
  /** When true, the first user video block can show download (same message as the conversation’s initial upload). */
  isInitialUploadVideoMessage?: boolean
  /** Signed URL to the original uploaded file — only used with `isInitialUploadVideoMessage`. */
  downloadVideoUrl?: string
  downloadFileName?: string
}

export const AgentMessageComponent = ({
  message,
  onSeekToTime,
  onPlaySegment,
  videoReference,
  videoUrl,
  mux,
  videoDuration,
  videoSources,
  isShowFunctionCalls = false,
  isInitialUploadVideoMessage = false,
  downloadVideoUrl,
  downloadFileName,
}: AgentMessageProperties): ReactElement => {
  /* --- Section 3: Derived Values --- */
  const isUser = message.role === 'user'
  const visibleContents = message.contents.filter(content => {
    return isShowFunctionCalls || (content.type !== 'function_call' && content.type !== 'function_result')
  })
  const hasMediaContent = visibleContents.some(content =>
    ['video_artifact', 'video_segment', 'video_side_by_side', 'youtube_video', 'frame_snapshot'].includes(content.type)
  )
  const contentKeyCounts = new Map<string, number>()
  const firstVideoArtifactIndex = visibleContents.findIndex(content => content.type === 'video_artifact')

  /* --- Section 6: Render --- */
  return (
    <div className={clsx(styles.message, isUser ? styles.messageUser : styles.messageAgent)}>
      <div
        className={clsx(
          styles.messageBubble,
          isUser ? styles.messageBubbleUser : styles.messageBubbleAgent,
          isUser && hasMediaContent && styles.messageBubbleUserHasMedia
        )}
      >
        <div className={styles.messageHeader}>
          <span className={styles.messageRole}>{isUser ? 'You' : 'Pocket Coach'}</span>
        </div>
        <div className={styles.messageContents}>
          {visibleContents.map((content, contentIndex) => {
            const signature = getContentSignature(content)
            const occurrenceCount = contentKeyCounts.get(signature) ?? 0
            contentKeyCounts.set(signature, occurrenceCount + 1)
            const contentKey =
              content.type === 'thinking'
                ? `${message.id}:thinking:${occurrenceCount}`
                : `${message.id}:${signature}:${occurrenceCount}`

            const allowDownload =
              isInitialUploadVideoMessage &&
              content.type === 'video_artifact' &&
              contentIndex === firstVideoArtifactIndex &&
              Boolean(downloadVideoUrl)

            return (
              <ContentRenderer
                key={contentKey}
                content={content}
                downloadFileName={allowDownload ? downloadFileName : undefined}
                downloadVideoUrl={allowDownload ? downloadVideoUrl : undefined}
                isThinkingOpen={message.thinkingOpen}
                onSeekToTime={onSeekToTime}
                onPlaySegment={onPlaySegment}
                videoReference={videoReference}
                videoUrl={videoUrl}
                mux={mux}
                videoDuration={videoDuration}
                videoSources={videoSources}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
